import { Filter } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import { DirectionsOutlined } from '@mui/icons-material';
import { Widget, WidgetContent, WidgetHeader, WidgetIcon, useScrollIntoView } from '@procureai/ui-mui';
import { isEmpty, isNil } from 'ramda';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GeoJSONSourceRaw } from 'react-map-gl';

import { I18nNamespace, ModuleEntityType } from '~/common/types';
import { CircularProgressCentered } from '~/components/CircularProgressCentered/CircularProgressCentered';
import { useConfig } from '~/components/ConfigProvider';
import { Map, MapMarkerVariant, MapProps } from '~/components/Map';
import { MapSourceId, MapViewState } from '~/components/Map/Map.types';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { PlaceholderWidget } from '~/components/PlaceholderWidget/PlaceholderWidget';
import { WidgetContentInner } from '~/routes/Analytics/Base/SupplierList/components/MapWidget/MapWidget.styled';

const markerVariants: MapProps['markerVariants'] = {
  known: MapMarkerVariant.PRIMARY,
};

export const SupplierLocationWidget = () => {
  const { entityId, entity } = useModuleContext();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'SupplierLocationWidget' });
  const { map, tenant } = useConfig();
  const [widgetRef, active] = useScrollIntoView({ hash: 'supplier-location' });

  const [viewState, setViewState] = useState<MapViewState>({
    latitude: tenant.geo.latitude,
    longitude: tenant.geo.longitude,
    zoom: 5,
  });

  const cube = entity === ModuleEntityType.SUPPLIER ? 'SummarySupplier' : 'SummaryCompany';

  const filters: Filter[] = [
    {
      member: `${cube}.id`,
      operator: 'equals',
      values: [entityId],
    },
  ];

  const query = useCubeQuery({
    filters,
    dimensions: [`${cube}.latitude`, `${cube}.longitude`, `${cube}.name`],
  });

  const nodes = query.resultSet?.rawData()?.map((segment) => ({
    latitude: Number(segment[`${cube}.latitude`]),
    longitude: Number(segment[`${cube}.longitude`]),
    name: segment[`${cube}.name`],
  }));

  const isNodesEmpty = isEmpty(nodes) || isNil(nodes);
  const isLoading = query.isLoading;

  const dataPoint: GeoJSONSourceRaw['data'] = {
    geometry: {
      coordinates: [nodes?.[0]?.longitude ?? 0, nodes?.[0]?.latitude ?? 0],
      type: 'Point',
    },
    properties: {
      id: entityId,
      name: nodes?.[0]?.name,
      relationship: 'known',
    },
    type: 'Feature',
  };

  const features: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
    type: 'FeatureCollection',
    features: [dataPoint],
  };

  return (
    <Widget ref={widgetRef} active={active} data-intercom-target="SupplierLocationWidget">
      <WidgetHeader title={t('title')} icon={<WidgetIcon icon={<DirectionsOutlined fontSize="small" />} />} />
      <WidgetContent disablePadding={!isNodesEmpty}>
        {!isNodesEmpty ? (
          <WidgetContentInner sx={{ height: 400 }}>
            <Map
              viewState={viewState}
              mapStyle={map.style}
              markerVariants={markerVariants}
              sources={[
                {
                  data: features,
                  id: MapSourceId.supplierGeoData,
                },
              ]}
              loading={query.isLoading}
              onViewStateSelect={setViewState}
            />
          </WidgetContentInner>
        ) : isLoading ? (
          <CircularProgressCentered sx={{ position: 'relative' }} />
        ) : (
          <PlaceholderWidget />
        )}
      </WidgetContent>
    </Widget>
  );
};
